import { useCallback, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { CREATE_QUESTION } from 'queries/question'
import { createQuestion, createQuestionVariables } from '__generatedTypes__/createQuestion'
import { CreateQuestionInput } from '__generatedTypes__/globalTypes'
import ItemCreatedModal from 'components/Modals/ItemCreatedModal'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form } from '../Form'
import { FormStateApi } from '../Form/consts'

export const Create = () => {
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [create] = useMutation<createQuestion, createQuestionVariables>(CREATE_QUESTION)
  const resetFormRef = useRef<(() => void) | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleSubmit = useCallback(
    async (question: CreateQuestionInput, formApi: FormStateApi) => {
      try {
        const { data } = await create({
          variables: {
            params: {
              ...question,
              history: populateHistoryWithUser(question.history),
            },
          },
        })

        if (data?.createQuestion.id) {
          setIsModalOpen(true)
          resetFormRef.current = formApi.restart
        } else {
          toast.error('Failed to create question')
        }
      } catch (error) {
        toast.error('Failed to create question')
      }
    },
    [populateHistoryWithUser, create]
  )

  const handleOpenQuestionList = useCallback(() => {
    setIsModalOpen(false)
    navigate(cmsRoutes.QUESTION_LIST)
  }, [navigate])

  const handleAddNewQuestion = useCallback(() => {
    setIsModalOpen(false)
    resetFormRef.current?.()
  }, [])

  if (loadingHistory) {
    return <PageLoader />
  }

  return (
    <>
      <Form onSubmit={handleSubmit} />
      <ItemCreatedModal
        open={isModalOpen}
        title="Question Created!"
        subtitle={
          <>
            The question successfully uploaded.
            <br />
            What do you want to do next?
          </>
        }
        firstOptionText="Go back to the Question page"
        secondOptionText="Add a New Question"
        onFirstOptionClick={handleOpenQuestionList}
        onSecondOptionClick={handleAddNewQuestion}
        onClose={handleOpenQuestionList}
      />
    </>
  )
}
